import { useEffect, useMemo } from 'react';
import { UseFormWatch } from 'react-hook-form';

import { setNumberOfItemsWithQuantity, setSubtotal } from 'stores/orderForm/orderForm';
import { createProductsRecord, OrderData, OrderFormValue } from 'support/helpers/orderForms/orderForms';
import { OrderFormDTO, OrderFormProductDTO } from 'support/types';

export const calculateSubtotal = (
  quantityPerProduct: Record<string, OrderFormValue>,
  productsRecord: Record<string, OrderFormProductDTO>,
) => {
  return Object.entries(quantityPerProduct).reduce((acc, [id, product]) => {
    return acc + (product.quantity || 0) * (productsRecord[id]?.discounted_unit_price ?? 0);
  }, 0);
};

const calculateNumberOfItems = (quantityPerProduct: Record<string, OrderFormValue>) => {
  return Object.values(quantityPerProduct).filter((product) => product.quantity && product.quantity > 0).length;
};

export const useOrderFormCalculations = (
  orderForm: OrderFormDTO['data'] | undefined,
  watch: UseFormWatch<OrderData>,
) => {
  const productsRecord = useMemo(() => createProductsRecord(orderForm), [orderForm]);

  useEffect(() => {
    const updateSubtotal = (formData: OrderData) => {
      const quantityPerProduct = formData.products || {};
      const subtotal = calculateSubtotal(quantityPerProduct, productsRecord);
      const numberOfItemsWithQuantity = calculateNumberOfItems(quantityPerProduct);

      // Update number of items with quantity in the store
      setNumberOfItemsWithQuantity(numberOfItemsWithQuantity);

      // Update subtotal in the store
      setSubtotal(subtotal);
    };

    const initialValues = watch();
    updateSubtotal(initialValues);

    const subscription = watch((value) => {
      updateSubtotal(value as OrderData);
    });

    return () => subscription.unsubscribe();
  }, [watch, productsRecord]);
};
